/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 * Expenses Analyzer Table
 *
 * @author Maxim Fedorov
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.16.0
 * @since 1.1.0
 */

import { FC } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { Table } from '../../../../components/common';
import { setTableSubRowMessage } from '../../../../components/Table/actions/tableActions';
import { IBasketItem } from '../../../../components/Table/components/footerModal/Basket/basketTypes';
import { ITableField, ITableReducer } from '../../../../components/Table/reducers/tableReducer';
import {
    makeSelectorCollapseTableSubRows,
    makeSelectorTableBasketAllIn,
    makeSelectorTableCount,
    makeSelectorTableData,
    makeSelectorTableFields,
    makeSelectorTableFilter,
    makeSelectorTableIsLoading,
    makeSelectorTableSort,
    makeSelectorTableSubRowsIds,
} from '../../../../components/Table/selectors/tableSelectors';
import TableColumnUtil from '../../../../components/Table/TableColumnUtil';
import {
    IFieldResize,
    ISetTableSortPayload,
    ISetTableSubRowMessagePayload,
    ISwitchCheckboxPayload,
    ITableFooterSize,
    TableNameType,
} from '../../../../components/Table/types/tableTypes';
import {
    ICommonTranslation,
    ICorporateTranslation,
    IPageExpensesAnalyzerTranslation,
} from '../../../../modules/internationalization/types';
import { guardComponentType } from '../../../../modules/role/reducer/guardReducer';
import { makeSelectGuardComponents } from '../../../../modules/role/selectors/guardSelector';
import { IRootReducer } from '../../../../reducers/rootReducer';
import { makeSelectorExpensesAnalyzerType } from '../../pageExpensesAnalyzerSelectors';
import { mapExpensesTypeToOnboarding } from '../../types/pageExpensesAnalyzerMapTypes';
import { EExpensesAnalyzerTypes } from '../../types/pageExpensesAnalyzerTypes';
import {
    analyzerColumnFilterType,
    ExpensesAnalyzerCellConfig,
    getExpensesAnalyzerColumnCellType,
    getExpensesAnalyzerColumnFixedType,
} from './Columns/Cells';
import ExpensesAnalyzerTableFooter from './ExpensesAnalyzerTableAccountFooter';
import { EExpensesAnalyzerField, ExpensesAnalyzerColumnNames } from './types/expensesAnalyzerTableTypes';


interface IAnalyzerTableProps {
    isDensity: boolean;
    isTableResizeEnabled: boolean;
    forceFooterUpdate: boolean;
    loadChart: () => void;
    loadTableList: () => void;
    loadTableSubRow: (payload: number) => void;
    onChangeFooterSize: (footerSize: ITableFooterSize) => void;
    saveResizedChange: (fields: IFieldResize[]) => void;
    sendTableFields: (tableFields: Array<ITableField<EExpensesAnalyzerField>>) => void;
    setTableSort: (payload: ISetTableSortPayload) => void;
    sideBarWidth: number;
    switchCheckbox: (payload: ISwitchCheckboxPayload) => void;
    tableCheckedItems: IBasketItem[];
    translation: IPageExpensesAnalyzerTranslation & ICommonTranslation & ICorporateTranslation;
    type: EExpensesAnalyzerTypes;
}

interface IConnectedProps {
    basketAllIn: boolean;
    collapseTableSubRows: boolean;
    componentsFeature: guardComponentType;
    counter: number;
    data: ITableReducer[TableNameType]['data'];
    expensesAnalyzerType: EExpensesAnalyzerTypes;
    fields: ITableReducer[TableNameType]['fields'];
    isLoading: boolean;
    selectedFilters: ITableReducer[TableNameType]['filter'];
    sort: ITableReducer[TableNameType]['sort'];
    subRowsIds: number[];
}

type TExpensesAnalyzerTableProps = IAnalyzerTableProps & IConnectedProps & IDispatchProps;

const ExpensesAnalyzerTable: FC<TExpensesAnalyzerTableProps> = (props: TExpensesAnalyzerTableProps) => {

    const tableColumnUtil = new TableColumnUtil<EExpensesAnalyzerField,
        IPageExpensesAnalyzerTranslation & ICorporateTranslation & ICommonTranslation>(
        {
            cellConfig: ExpensesAnalyzerCellConfig,
            componentsFeature: props.componentsFeature,
            columnNames: ExpensesAnalyzerColumnNames,
            fields: props.fields as Array<ITableField<EExpensesAnalyzerField>>,
            getColumnCellType: getExpensesAnalyzerColumnCellType,
            getColumnFilterType: analyzerColumnFilterType,
            getColumnFixedType: getExpensesAnalyzerColumnFixedType,
            isTableResizeEnabled: props.isTableResizeEnabled,
            translation: props.translation,
        });

    const handleCollapseRow = (subRowId: number) => {
        if (!props.subRowsIds.includes(subRowId)) {
            props.setTableSubRowMessage({ subRowId, message: props.translation.commonTable.loading });
        }
    };

    const handleExpandRow = (subRowId: number) => {
        if (!props.subRowsIds.includes(subRowId)) {
            props.loadTableSubRow(subRowId);
        }
    };

    return (
        <Table
            basketAllIn={props.basketAllIn}
            collapseTableSubRows={props.collapseTableSubRows}
            columns={tableColumnUtil.customizeTableColumns()}
            counter={props.counter}
            data={props.data}
            disableCheckedAll={!props.data.length}
            disableSortCheckbox={true}
            fields={props.fields as Array<ITableField<EExpensesAnalyzerField>>}
            footer={<ExpensesAnalyzerTableFooter
                loadChart={props.loadChart}
                type={props.type}
            />}
            forceFooterUpdate={props.forceFooterUpdate}
            getProps={() => ({ className: 'table-with-checkboxes' })}
            isDensity={props.isDensity}
            isLoading={props.isLoading}
            isTableFooter={true}
            loadTableList={props.loadTableList}
            onboarding={mapExpensesTypeToOnboarding[props.expensesAnalyzerType]?.table}
            saveResizedChange={props.saveResizedChange}
            setTableSort={props.setTableSort}
            showCheckedColumn={true}
            sideBarWidth={props.sideBarWidth}
            sort={props.sort}
            switchCheckbox={props.switchCheckbox}
            tableCheckedItems={props.tableCheckedItems}
            tableDataName={TableNameType.expensesAnalyzer}
            tableName={TableNameType.expensesAnalyzer}
            onChangeFooterSize={props.onChangeFooterSize}
            onCollapseRow={handleCollapseRow}
            onExpandRow={handleExpandRow}
        />
    );
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    basketAllIn: makeSelectorTableBasketAllIn(TableNameType.expensesAnalyzer),
    collapseTableSubRows: makeSelectorCollapseTableSubRows(TableNameType.expensesAnalyzer),
    componentsFeature: makeSelectGuardComponents(),
    expensesAnalyzerType: makeSelectorExpensesAnalyzerType(),
    counter: makeSelectorTableCount(TableNameType.expensesAnalyzer),
    data: makeSelectorTableData(TableNameType.expensesAnalyzer),
    fields: makeSelectorTableFields(TableNameType.expensesAnalyzer),
    isLoading: makeSelectorTableIsLoading(TableNameType.expensesAnalyzer),
    selectedFilters: makeSelectorTableFilter(TableNameType.expensesAnalyzer),
    sort: makeSelectorTableSort(TableNameType.expensesAnalyzer),
    subRowsIds: makeSelectorTableSubRowsIds(TableNameType.expensesAnalyzer),
});

interface IDispatchProps {
    setTableSubRowMessage: (payload: ISetTableSubRowMessagePayload) => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setTableSubRowMessage: (payload) => dispatch(setTableSubRowMessage(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExpensesAnalyzerTable);
